import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import './NotificationModal.css';
import notificationBackground from '../assets/images/loginpage.png';
import { apiRequest } from '../utils/api';

const NotificationModal = ({ show, onClose, onSubmit, submitting, error }) => {
  const navigate = useNavigate();
  const [message, setMessage] = useState('');
  const [status, setStatus] = useState('active');
  const [recipientType, setRecipientType] = useState('employee');
  const [employeeQuery, setEmployeeQuery] = useState('');
  const [employeeResults, setEmployeeResults] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [employeeSearchLoading, setEmployeeSearchLoading] = useState(false);
  const [employeeSearchError, setEmployeeSearchError] = useState(null);
  const [localError, setLocalError] = useState(null);

  useEffect(() => {
    if (show) {
      setMessage('');
      setStatus('active');
      setRecipientType('employee');
      setEmployeeQuery('');
      setEmployeeResults([]);
      setSelectedEmployee(null);
      setEmployeeSearchError(null);
      setLocalError(null);
    }
  }, [show]);

  useEffect(() => {
    setLocalError(null);
    if (recipientType !== 'employee') {
      setSelectedEmployee(null);
      setEmployeeQuery('');
      setEmployeeResults([]);
      setEmployeeSearchError(null);
    }
  }, [recipientType]);

  useEffect(() => {
    if (!show || recipientType !== 'employee') {
      return undefined;
    }

    const trimmed = employeeQuery.trim();
    if (!trimmed || trimmed.length < 2) {
      setEmployeeResults([]);
      setEmployeeSearchError(null);
      return undefined;
    }

    const handle = setTimeout(async () => {
      try {
        setEmployeeSearchLoading(true);
        setEmployeeSearchError(null);
        const response = await apiRequest(
          `/api/admin/notifications/recipients/employees?query=${encodeURIComponent(trimmed)}`,
          {},
          navigate,
        );
        setEmployeeResults(Array.isArray(response.data) ? response.data : []);
      } catch (searchError) {
        setEmployeeResults([]);
        setEmployeeSearchError(searchError.message || 'Unable to search employees');
      } finally {
        setEmployeeSearchLoading(false);
      }
    }, 350);

    return () => {
      clearTimeout(handle);
    };
  }, [employeeQuery, navigate, recipientType, show]);

  const selectedEmployeeLabel = useMemo(() => {
    if (!selectedEmployee) {
      return '';
    }

    const identifiers = [
      selectedEmployee.permanentEmployeeId,
      selectedEmployee.tempEmployeeId,
    ].filter(Boolean);
    const resolvedId = identifiers.length
      ? Array.from(new Set(identifiers)).join(' / ')
      : selectedEmployee.employeeCode || `#${selectedEmployee.id}`;
    const mobile = selectedEmployee.mobile || 'N/A';
    const name = selectedEmployee.name || 'Employee';
    return `${resolvedId} – ${name} – ${mobile}`;
  }, [selectedEmployee]);

  if (!show) {
    return null;
  }

  const handleSubmit = (event) => {
    event.preventDefault();
    const trimmedMessage = message.trim();
    if (!trimmedMessage) {
      setLocalError('Notification message is required');
      return;
    }

    let payloadRecipient = {};
    if (recipientType === 'employee') {
      if (!selectedEmployee?.id) {
        setLocalError('Please select an employee for this notification');
        return;
      }
      payloadRecipient = { employeeId: Number(selectedEmployee.id) };
    } else if (recipientType === 'allEmployees') {
      payloadRecipient = { employeeId: 0 };
    } else {
      setLocalError('Please choose a valid recipient type');
      return;
    }

    setLocalError(null);
    onSubmit({
      message: trimmedMessage,
      status,
      ...payloadRecipient,
    });
  };

  return (
    <div
      className="notification-modal-backdrop"
      role="dialog"
      aria-modal="true"
      style={{ backgroundImage: `url(${notificationBackground})` }}
    >
      <div className="notification-modal-overlay" />
      <div className="notification-modal-card">
        <button
          type="button"
          className="btn-close notification-modal-close"
          aria-label="Close"
          onClick={onClose}
          disabled={submitting}
        />
        <div className="notification-modal-content">
          <h3 className="fw-semibold mb-4 text-center">Add Notification</h3>
          <form onSubmit={handleSubmit} className="notification-modal-form">
            <div className="mb-4">
              <label className="form-label fw-semibold">Notification Message :</label>
              <textarea
                className="form-control"
                rows={4}
                value={message}
                onChange={(event) => setMessage(event.target.value)}
                placeholder="Enter notification message"
                disabled={submitting}
              />
            </div>
            <div className="mb-4">
              <label className="form-label fw-semibold">Recipient :</label>
              <div className="d-flex flex-column gap-2">
                <div className="form-check">
                  <input
                    className="form-check-input"
                    type="radio"
                    name="notificationRecipientType"
                    id="notificationRecipientEmployee"
                    value="employee"
                    checked={recipientType === 'employee'}
                    onChange={(event) => setRecipientType(event.target.value)}
                    disabled={submitting}
                  />
                  <label className="form-check-label" htmlFor="notificationRecipientEmployee">
                    Specific Employee
                  </label>
                </div>
                <div className="form-check">
                  <input
                    className="form-check-input"
                    type="radio"
                    name="notificationRecipientType"
                    id="notificationRecipientAllEmployees"
                    value="allEmployees"
                    checked={recipientType === 'allEmployees'}
                    onChange={(event) => setRecipientType(event.target.value)}
                    disabled={submitting}
                  />
                  <label className="form-check-label" htmlFor="notificationRecipientAllEmployees">
                    All Employees
                  </label>
                </div>
              </div>
            </div>
            {recipientType === 'employee' && (
              <div className="mb-4">
                <label className="form-label fw-semibold" htmlFor="notificationEmployeeSearch">
                  Specific Employee :
                </label>
                <input
                  id="notificationEmployeeSearch"
                  type="text"
                  className="form-control"
                  value={employeeQuery}
                  onChange={(event) => setEmployeeQuery(event.target.value)}
                  placeholder="Search by employee name or mobile number"
                  disabled={submitting}
                />
                <div className="form-text">
                  Start typing to search employees in the selected company by name or mobile number.
                </div>

                <div className="notification-employee-results mt-3">
                  {employeeSearchLoading ? (
                    <div className="text-muted small">Searching employees...</div>
                  ) : employeeSearchError ? (
                    <div className="text-danger small">{employeeSearchError}</div>
                  ) : employeeQuery.trim().length < 2 ? (
                    <div className="text-muted small">Enter at least 2 characters to search.</div>
                  ) : employeeResults.length === 0 ? (
                    <div className="text-muted small">No employees match your search.</div>
                  ) : (
                    <div className="notification-employee-options">
                      {employeeResults.map((employee) => {
                        const identifiers = [
                          employee.permanentEmployeeId,
                          employee.tempEmployeeId,
                        ].filter(Boolean);
                        const displayId =
                          identifiers.length > 0
                            ? Array.from(new Set(identifiers)).join(' / ')
                            : employee.employeeCode || `#${employee.id}`;
                        const isSelected = selectedEmployee?.id === employee.id;

                        return (
                          <button
                            key={employee.id}
                            type="button"
                            className={`notification-employee-option ${isSelected ? 'active' : ''}`}
                            onClick={() => setSelectedEmployee(employee)}
                            disabled={submitting}
                          >
                            <div className="notification-employee-option-main">
                              <span className="fw-semibold">{displayId}</span>
                              <span className="text-muted">{employee.name || 'Employee'}</span>
                            </div>
                            <div className="notification-employee-option-meta">
                              <span className="badge bg-light text-muted text-uppercase">{employee.idType}</span>
                              <span className="text-muted">{employee.mobile || 'N/A'}</span>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>

                {selectedEmployee && (
                  <div className="alert alert-light border mt-3 py-2 mb-0" role="status">
                    <div className="small text-muted">Selected employee</div>
                    <div className="fw-semibold">{selectedEmployeeLabel}</div>
                  </div>
                )}
              </div>
            )}
            {(localError || error) && (
              <div className="alert alert-danger py-2" role="alert">
                {localError || error}
              </div>
            )}
            <div className="d-grid">
              <button type="submit" className="btn btn-danger" disabled={submitting}>
                {submitting ? 'Adding Notification...' : 'Add Notification'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

NotificationModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  submitting: PropTypes.bool,
  error: PropTypes.string,
};

NotificationModal.defaultProps = {
  submitting: false,
  error: null,
};

export default NotificationModal;

